const LOCAL_DEVELOPMENT_API_BASE_URL = "http://localhost:4000";
const PRODUCTION_API_BASE_URL = "https://api.mahalaairtime.com";

const DEVELOPMENT_APP_ENVS = new Set(["development", "dev", "local", "test"]);
const LOCAL_API_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);
const PRODUCTION_API_ORIGIN = new URL(PRODUCTION_API_BASE_URL).origin;

const defaultConfig = {
  apiBaseUrl: LOCAL_DEVELOPMENT_API_BASE_URL,
  publicSiteUrl: "https://mahalaairtime.com",
  firebase: {
    apiKey: "AIzaSyBsGE_0E1ODa_ZsSJyQVVl5QoxUsaMn604",
    authDomain: "mahala-airtime.firebaseapp.com",
    databaseURL: "https://mahala-airtime-default-rtdb.firebaseio.com",
    projectId: "mahala-airtime",
    storageBucket: "mahala-airtime.appspot.com",
    messagingSenderId: "402405232219",
    appId: "1:402405232219:web:a75d29b7ec2914f82bb97e",
    measurementId: "G-N4PYXTF8V1",
  },
  appEnv: "production",
};

const runtimeConfig =
  typeof window !== "undefined" ? window.__APP_CONFIG__ || {} : {};

const env = process.env || {};

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

export const isValidApiBaseUrl = (value, { appEnv = "production" } = {}) => {
  const candidate = String(value || "").trim();

  try {
    const parsed = new URL(candidate);
    const isSafeUrl =
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      !parsed.username &&
      !parsed.password &&
      !parsed.search &&
      !parsed.hash;

    if (!isSafeUrl) return false;

    const normalizedAppEnv = String(appEnv || "production").toLowerCase();
    const isApprovedProductionUrl = parsed.origin === PRODUCTION_API_ORIGIN;
    const isApprovedDevelopmentUrl =
      DEVELOPMENT_APP_ENVS.has(normalizedAppEnv) && LOCAL_API_HOSTS.has(parsed.hostname);

    return isApprovedProductionUrl || isApprovedDevelopmentUrl;
  } catch (error) {
    return false;
  }
};

export const resolveApiBaseUrl = ({ runtime = runtimeConfig, environment = env } = {}) => {
  const appEnv = String(
    runtime.APP_ENV || environment.VUE_APP_ENV || environment.NODE_ENV || "development"
  ).toLowerCase();
  const configuredValue = runtime.API_BASE_URL || environment.VUE_APP_API_BASE_URL;
  const isDevelopment = ["development", "dev", "local", "test"].includes(appEnv);

  if (isValidApiBaseUrl(configuredValue, { appEnv })) {
    return {
      value: trimTrailingSlash(configuredValue),
      valid: true,
      source: runtime.API_BASE_URL ? "runtime" : "build",
      appEnv,
    };
  }

  const message = configuredValue
    ? `[runtime-config] Invalid API_BASE_URL. Production must use ${PRODUCTION_API_BASE_URL} over HTTPS; development may use a local localhost or 127.0.0.1 API.`
    : "[runtime-config] API_BASE_URL is missing.";

  if (isDevelopment) {
    console.warn(`${message} Using the local development API at ${LOCAL_DEVELOPMENT_API_BASE_URL}.`);
    return {
      value: LOCAL_DEVELOPMENT_API_BASE_URL,
      valid: true,
      source: "local-fallback",
      appEnv,
      warning: message,
    };
  }

  console.error(
    `${message} Configure window.__APP_CONFIG__.API_BASE_URL before starting the application.`
  );
  return { value: "", valid: false, source: "invalid", appEnv, error: message };
};

const resolvedApiBaseUrl = resolveApiBaseUrl();

export const appConfig = {
  apiBaseUrl: resolvedApiBaseUrl.value,
  apiBaseUrlValid: resolvedApiBaseUrl.valid,
  apiBaseUrlSource: resolvedApiBaseUrl.source,
  runtimeConfigError: resolvedApiBaseUrl.error || "",
  publicSiteUrl:
    runtimeConfig.PUBLIC_SITE_URL || env.VUE_APP_PUBLIC_SITE_URL || defaultConfig.publicSiteUrl,
  firebase: {
    apiKey:
      runtimeConfig.FIREBASE_API_KEY || env.VUE_APP_FIREBASE_API_KEY || defaultConfig.firebase.apiKey,
    authDomain:
      runtimeConfig.FIREBASE_AUTH_DOMAIN ||
      env.VUE_APP_FIREBASE_AUTH_DOMAIN ||
      defaultConfig.firebase.authDomain,
    databaseURL:
      runtimeConfig.FIREBASE_DATABASE_URL ||
      env.VUE_APP_FIREBASE_DATABASE_URL ||
      defaultConfig.firebase.databaseURL,
    projectId:
      runtimeConfig.FIREBASE_PROJECT_ID ||
      env.VUE_APP_FIREBASE_PROJECT_ID ||
      defaultConfig.firebase.projectId,
    storageBucket:
      runtimeConfig.FIREBASE_STORAGE_BUCKET ||
      env.VUE_APP_FIREBASE_STORAGE_BUCKET ||
      defaultConfig.firebase.storageBucket,
    messagingSenderId:
      runtimeConfig.FIREBASE_MESSAGING_SENDER_ID ||
      env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID ||
      defaultConfig.firebase.messagingSenderId,
    appId:
      runtimeConfig.FIREBASE_APP_ID || env.VUE_APP_FIREBASE_APP_ID || defaultConfig.firebase.appId,
    measurementId:
      runtimeConfig.FIREBASE_MEASUREMENT_ID ||
      env.VUE_APP_FIREBASE_MEASUREMENT_ID ||
      defaultConfig.firebase.measurementId,
  },
  appEnv: resolvedApiBaseUrl.appEnv || runtimeConfig.APP_ENV || env.NODE_ENV || defaultConfig.appEnv,
};

export const hasRequiredFirebaseConfig =
  Boolean(appConfig.firebase.apiKey) &&
  Boolean(appConfig.firebase.authDomain) &&
  Boolean(appConfig.firebase.projectId) &&
  Boolean(appConfig.firebase.appId);

if ((env.NODE_ENV || "development") !== "production" && !hasRequiredFirebaseConfig) {
  console.warn(
    "[runtime-config] Missing Firebase settings. Falling back to bundled defaults where available."
  );
}

export default appConfig;
